'use client';

import useFormFillPage from '@/hooks/use-form-fill-page';
import { getFullName } from '@/models/user/user';
import { BACKEND_ERROR_CODES } from '@/types/error';
import { useMutation } from '@tanstack/react-query';
import * as RPNInput from 'react-phone-number-input';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import RichTextContent from '@/components/rich-text-editor/rich-text-content';
import { TemplateColor } from '@/components/template-page-layout/template-page-layout.types';
import { Button } from '@/components/ui/button';

import { toast } from '@/shared/components';
import { trackPixelEvent } from '@/shared/components/seo/meta-pixel';
import { COPY } from '@/shared/constants/copies';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { generateOTP } from '@/shared/services/auth';
import { ISGetFormFillingDetailsResponse } from '@/shared/services/forms/forms.types';
import { formFillSubmit, uploadFile } from '@/shared/services/submissions';
import { formFillSubmitWithoutEmailVerification } from '@/shared/services/submissions/submissions';
import {
	IFormFillSubmitPayload,
	ISUploadFileDraftFileMetadata,
	IVideoFileMetadata,
} from '@/shared/services/submissions/submissions.types';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { normalizeEmail } from '@/shared/utils/validation';

import { getApiErrorDetails } from '@/utils/axios';
import { sanitizeFileName } from '@/utils/file';
import { generateFormSchemaFromInputFields } from '@/utils/schema';
import { TEMPLATE_IDS } from '@/utils/template-utils';

import { useFormFillDraft } from '../../hooks/use-form-fill-draft';
import {
	IInputFieldIdToErrorMap,
	IInputFieldIdToValueMap,
	IInputFieldIdsToDivMap,
	IUploadFileInputFieldIdToStatusMap,
} from '../../types/form-filling-types';
import RegisterModal from '../common/auth/register-modal';
import ClosedState from '../common/closed-state';
import FillingInputField from '../common/filling-input-field';
import FormFillHeader from '../common/form-fill-header';
import FormFillInputsSection from '../common/form-fill-inputs-section';
import FormWidgetsDisplay from '../common/form-widgets-display';
import InputFieldError from '../common/input-field-error';
import SuccessState from '../common/success-state';
import UnsavedVideoDialog from '../common/unsaved-video-dialog';

interface Props {
	className?: string;
	viewOnlyMode?: boolean;
	viewOnlyFormDetails?: ISGetFormFillingDetailsResponse;
	hideSubmitButton?: boolean;
}

function FillingSection({
	className = '',
	viewOnlyMode = false,
	hideSubmitButton = false,
	viewOnlyFormDetails,
}: Props) {
	const {
		updatedFormFillDetails,
		invalidateUserRelatedQueries,
		refetchFormFillDetails,
		updatedUserDetails,
		isUserDetailsRefetching,
		isFormFilledByUserDetailsRefetching,
		updatedFormFilledByUserDetails,
	} = useFormFillPage();

	const formFillDetails = viewOnlyMode ? viewOnlyFormDetails : updatedFormFillDetails;
	const formId = formFillDetails?.id;

	const [uploadFileInputFieldIdToStatusMap, setUploadFileInputFieldIdToStatusMap] =
		useState<IUploadFileInputFieldIdToStatusMap>({});

	const [inputFieldIdToErrorMap, setInputFieldIdToErrorMap] = useState<IInputFieldIdToErrorMap>({});
	const [inputFieldIdToValueMap, setInputFieldIdToValueMap] = useState<IInputFieldIdToValueMap>({});
	const [videoEditModeMap, setVideoEditModeMap] = useState<Record<string, boolean>>({});
	const [unsavedVideoMap, setUnsavedVideoMap] = useState<Record<string, boolean>>({});
	const [showUnsavedVideoDialog, setShowUnsavedVideoDialog] = useState(false);
	const [isVideoUploading, setIsVideoUploading] = useState(false);
	const [activeRecordingFieldId, setActiveRecordingFieldId] = useState<string | null>(null);

	const [openOtpModal, setOpenOtpModal] = useState(false);
	const [isFormJustSubmitted, setIsFormJustSubmitted] = useState<boolean>(false);

	const [isHandlingFormMismatch, setIsHandlingFormMismatch] = useState(false);

	const inputFieldIdsToDivMapRef = useRef<IInputFieldIdsToDivMap>({});
	const pendingFormOutdatedDisplayRef = useRef(false);

	const { clearDraft } = useFormFillDraft({
		formId,
		viewOnlyMode,
		values: inputFieldIdToValueMap,
		onHydrate: setInputFieldIdToValueMap,
	});

	const {
		mutate: triggerFormFillSubmit,
		reset: resetFormFillSubmitMutation,
		isPending: isFormFillSubmitMutationPending,
		error: formFillSubmitMutationError,
	} = useMutation({
		mutationFn: updatedFormFillDetails?.emailVerificationRequired
			? formFillSubmit
			: formFillSubmitWithoutEmailVerification,
		retry: undefined,
		onSuccess: () => {
			setIsFormJustSubmitted(true);
			emitMixpanelEvent(mixpanelEvents.COMPLETED_FORM_SUBMITTED, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formFillDetails?.id,
				form_name: formFillDetails?.name,
			});

			trackPixelEvent('Lead');
			clearDraft();
		},
		onError: async (error) => {
			const errorDetails = getApiErrorDetails(error);

			if (errorDetails?.errorCode === BACKEND_ERROR_CODES.FORM_OUTDATED) {
				pendingFormOutdatedDisplayRef.current = true;
				setIsHandlingFormMismatch(true);
				await refetchFormFillDetails();
				toast({
					title: 'Some fields need your attention.',
					variant: 'destructive',
				});
				setIsHandlingFormMismatch(false);
				return;
			}

			if (email && errorDetails?.errorStatusCode === 401)
				otpMutation.mutate({
					email: normalizeEmail(email),
				});

			// Track form submission error
			emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_SUBMIT_ERROR, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formFillDetails?.id,
				form_name: formFillDetails?.name,
				error_count: 1,
				all_errors: errorDetails.errorMessage,
				error_messages: errorDetails.errorMessage,
			});
		},
	});

	const otpMutation = useMutation({
		mutationFn: generateOTP,
		onSuccess: () => {
			setOpenOtpModal(true);
		},
		onError: (error) => {
			const errorDetails = getApiErrorDetails(error);
			const emailFieldName = emailInputFieldId
				? (inputFieldIdToQuestionMap?.[emailInputFieldId] ?? emailInputFieldId)
				: 'email';

			// Track OTP generation error
			emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_OTP_ERROR, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formFillDetails?.id,
				form_name: formFillDetails?.name,
				error_message: errorDetails.errorMessage,
				field_name: emailFieldName,
			});
		},
		retry: undefined,
	});

	const fileUploadMutation = useMutation({
		mutationFn: uploadFile,
		onMutate: (variables) => {
			setUploadFileInputFieldIdToStatusMap((prev) => ({
				...prev,
				[variables.fieldId]: 'pending',
			}));
		},
		onSuccess: (response, variables) => {
			setUploadFileInputFieldIdToStatusMap((prev) => ({
				...prev,
				[variables.fieldId]: 'success',
			}));

			setInputFieldIdToValueMap((prev) => ({
				...prev,
				[variables.fieldId]: response.draftFileMetadata,
			}));
		},
		onError: (error, variables) => {
			const errorDetails = getApiErrorDetails(error);

			setUploadFileInputFieldIdToStatusMap((prev) => ({
				...prev,
				[variables.fieldId]: 'error',
			}));

			setInputFieldIdToErrorMap((prev) => ({
				...prev,
				[variables.fieldId]: errorDetails.errorMessage,
			}));

			// Track file upload error
			const fieldName = inputFieldIdToQuestionMap[variables.fieldId] ?? variables.fieldId;
			emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_FILE_UPLOAD_ERROR, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formFillDetails?.id,
				form_name: formFillDetails?.name,
				error_message: errorDetails.errorMessage,
				field_name: fieldName,
			});
		},
		retry: undefined,
	});

	const formTitle = formFillDetails?.name || 'Untitled form';
	const formDescription = formFillDetails?.description ?? '';

	// Get template color
	const templateColor: TemplateColor = useMemo(() => {
		return formFillDetails?.templateId === TEMPLATE_IDS.MINIMAL && formFillDetails?.templateColor
			? formFillDetails.templateColor
			: 'blue';
	}, [formFillDetails]);

	const inputFields = useMemo(
		() => formFillDetails?.inputFields?.filter((inputField) => !!inputField.valid) ?? [],
		[formFillDetails]
	);

	const inputFieldIdToQuestionMap = useMemo(() => {
		return inputFields.reduce<Record<string, string>>((acc, field) => {
			acc[field.id] = field.question ?? field.id;
			return acc;
		}, {});
	}, [inputFields]);

	const formValidationSchema = useMemo(
		() => generateFormSchemaFromInputFields(inputFields),
		[inputFields]
	);

	const isUploadingFiles = useMemo(() => {
		return Object.values(uploadFileInputFieldIdToStatusMap).some((status) => status === 'pending');
	}, [uploadFileInputFieldIdToStatusMap]);

	const isFormFillSubmitLoading =
		isFormFillSubmitMutationPending ||
		otpMutation.isPending ||
		isUserDetailsRefetching ||
		isFormFilledByUserDetailsRefetching ||
		isHandlingFormMismatch;

	const disableActions = viewOnlyMode
		? false
		: isFormFillSubmitLoading ||
			isUploadingFiles ||
			isVideoUploading ||
			Object.values(videoEditModeMap).some(Boolean) ||
			formFillDetails?.status === 'DRAFT';

	const displayClosedState = formFillDetails?.status === 'CLOSED';

	const displaySuccessState =
		(isFormJustSubmitted ||
			(updatedFormFilledByUserDetails?.alreadyFilled &&
				!updatedFormFillDetails?.multipleSubmissions)) &&
		!displayClosedState;

	const displayMultipleSubmissionsNotAllowedState =
		!isFormJustSubmitted && !updatedFormFillDetails?.multipleSubmissions && !displayClosedState;

	const displayFormInputFields = !displayClosedState && !displaySuccessState;

	const formFillSubmitError = useMemo(() => {
		const formFillSubmitError = formFillSubmitMutationError;
		const otpError = otpMutation.error;

		if (otpError) {
			const { errorMessage, errorStatusCode } = getApiErrorDetails(otpError);

			if (errorStatusCode === 500) {
				const normalizedMessage = errorMessage.trimEnd().endsWith('.')
					? errorMessage.trimEnd()
					: `${errorMessage.trimEnd()}.`;
				return `${normalizedMessage} Please re-check the email and try again.`;
			} else if (errorStatusCode === 422) {
				return 'This email could not be found. Please re-check the email and try again.';
			}

			return errorMessage;
		}
		if (formFillSubmitError) {
			const { errorMessage, errorCode } = getApiErrorDetails(formFillSubmitError);
			if (errorCode === BACKEND_ERROR_CODES.FORM_OUTDATED) return null;
			return errorMessage;
		}

		return null;
	}, [formFillSubmitMutationError, otpMutation.error]);

	const nameInputFieldId = useMemo(() => {
		return inputFields.find((inputField) => inputField.preDefinedInputType === 'NAME')?.id;
	}, [inputFields]);

	const emailInputFieldId = useMemo(() => {
		return inputFields.find((inputField) => inputField.preDefinedInputType === 'EMAIL')?.id;
	}, [inputFields]);

	const email = useMemo(() => {
		return emailInputFieldId ? (inputFieldIdToValueMap[emailInputFieldId] as string) : null;
	}, [emailInputFieldId, inputFieldIdToValueMap]);

	const name = useMemo(() => {
		return nameInputFieldId ? (inputFieldIdToValueMap[nameInputFieldId] as string) : null;
	}, [nameInputFieldId, inputFieldIdToValueMap]);

	const handleCallFormFillSubmitMutation = () => {
		if (!formFillDetails) return;

		// Generate file name based on person's name and form ID
		// Variables removed as we now use actual S3 paths instead of generating file names

		const payload: IFormFillSubmitPayload = {
			form_id: formFillDetails.id,
			values: Object.keys(inputFieldIdToValueMap).map((fieldId) => {
				const value = inputFieldIdToValueMap[fieldId];

				if (typeof value === 'string') {
					return {
						id: fieldId,
						answer: value.trim(),
					};
				} else if (value instanceof Array || typeof value === 'number' || value === null) {
					return {
						id: fieldId,
						answer: value,
					};
				} else if (value instanceof Object && 'draftFilePath' in value) {
					const draftFileMetadata = value as ISUploadFileDraftFileMetadata;

					// Keep original format for non-video file uploads
					return {
						id: fieldId,
						draft_file_metadata: {
							content_type: draftFileMetadata.contentType,
							draft_file_path: draftFileMetadata.draftFilePath,
							original_filename: draftFileMetadata.originalFilename,
						},
					};
				} else if (value instanceof Object && 'file_path' in value && 'file_name' in value) {
					// This is the new video format
					const videoMetadata = value as IVideoFileMetadata;
					return {
						id: fieldId,
						draft_video_metadata: {
							file_path: `/${videoMetadata.file_name}`,
							file_name: videoMetadata.file_name,
							duration: videoMetadata.duration,
						},
					};
				} else if (value instanceof Blob) {
					// For now, we'll just store a placeholder for the video
					// In a real implementation, you'd want to upload this to your server
					return {
						id: fieldId,
						answer: 'Video recorded', // Placeholder for now
					};
				} else if (
					typeof value === 'string' &&
					(value as string).startsWith('draft-submissions/')
				) {
					// This is a legacy video path from S3 upload - use the actual path
					let videoPath = value as string;
					let fileName = videoPath.split('/').pop() || 'video.webm';

					// Fix the template variable issue - if path contains ${filename} or $%7Bfilename%7D
					if (fileName.includes('${filename}') || fileName.includes('$%7Bfilename%7D')) {
						fileName = `${fieldId}.webm`;
						videoPath = videoPath.replace(/\$\{filename\}|\$%7Bfilename%7D/g, fileName);
					}

					// Ensure the filename has the .webm extension
					if (!fileName.endsWith('.webm')) {
						fileName = `${fileName}.webm`;
					}

					return {
						id: fieldId,
						draft_video_metadata: {
							file_path: `/${fileName}`, // Use the full path
							file_name: fileName,
							duration: 0, // Default duration for legacy videos
						},
					};
				} else if (
					value &&
					typeof value === 'object' &&
					'draftFilePath' in value &&
					'contentType' in value
				) {
					// This is a legacy video metadata object from S3 upload
					const draftFileMetadata = value as ISUploadFileDraftFileMetadata;
					let videoPath = draftFileMetadata.draftFilePath;
					let fileName = videoPath.split('/').pop() || 'video.webm';

					// Fix the template variable issue - if path contains ${filename} or $%7Bfilename%7D
					if (fileName.includes('${filename}') || fileName.includes('$%7Bfilename%7D')) {
						fileName = `${fieldId}.webm`;
						videoPath = videoPath.replace(/\$\{filename\}|\$%7Bfilename%7D/g, fileName);
					}

					// Ensure the filename has the .webm extension
					if (!fileName.endsWith('.webm')) {
						fileName = `${fileName}.webm`;
					}

					return {
						id: fieldId,
						draft_video_metadata: {
							file_path: `/${fileName}`, // Use the full path
							file_name: fileName,
							duration: 0, // Default duration for legacy videos
						},
					};
				}

				return {
					id: fieldId,
					answer: null,
				};
			}),
		};

		triggerFormFillSubmit(payload);
	};

	const handleRegisterSuccess = async () => {
		setOpenOtpModal(false);

		const { updatedFormFilledByUserDetailsRes } = await invalidateUserRelatedQueries();

		if (
			updatedFormFilledByUserDetailsRes?.alreadyFilled &&
			!updatedFormFillDetails?.multipleSubmissions
		)
			return;

		handleCallFormFillSubmitMutation();
	};

	const handleSubmission = () => {
		if (disableActions) return;

		const userLoggedIn = !!updatedUserDetails;

		if (!userLoggedIn) {
			if (!email) return;

			if (formFillDetails?.emailVerificationRequired === false) {
				handleCallFormFillSubmitMutation();
				return;
			}

			otpMutation.mutate({
				email: normalizeEmail(email),
			});

			return;
		}

		handleCallFormFillSubmitMutation();
	};

	const handleUploadFile = (fieldId: string) => (file: File) => {
		if (!formFillDetails) return;

		const sanitizedFile = new File([file], sanitizeFileName(file.name), { type: file.type });

		fileUploadMutation.mutate({
			fieldId: fieldId,
			file: sanitizedFile,
			formId: formFillDetails.id,
			isReferral: false,
		});

		setInputFieldIdToErrorMap((prev) => ({
			...prev,
			[fieldId]: '',
		}));
	};

	const handleRemoveFile = (fieldId: string) => () => {
		setUploadFileInputFieldIdToStatusMap((prev) => ({
			...prev,
			[fieldId]: 'idle',
		}));

		setInputFieldIdToValueMap((prev) => ({
			...prev,
			[fieldId]: null,
		}));

		setInputFieldIdToErrorMap((prev) => ({
			...prev,
			[fieldId]: '',
		}));
	};

	const handleFileError = (fieldId: string) => (error: string) => {
		setInputFieldIdToErrorMap((prev) => ({
			...prev,
			[fieldId]: error,
		}));

		// Track file validation error
		const fieldName = inputFieldIdToQuestionMap[fieldId] ?? fieldId;
		emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_FILE_UPLOAD_ERROR, {
			feature: MIXPANEL_APP_FEATURES.FORM,
			form_id: formFillDetails?.id,
			form_name: formFillDetails?.name,
			error_message: error,
			field_name: fieldName,
		});
	};

	const handleChangeInputFieldValue =
		(fieldId: string) => (value: string | string[] | undefined) => {
			setInputFieldIdToValueMap((prev) => ({
				...prev,
				[fieldId]: !!value?.toString().trim().length ? value : null,
			}));
			setInputFieldIdToErrorMap((prev) => ({
				...prev,
				[fieldId]: '',
			}));
		};

	const handleChangeNumberFieldValue = (fieldId: string) => (value: string | undefined) => {
		setInputFieldIdToValueMap((prev) => ({
			...prev,
			[fieldId]: !!value?.toString().trim().length ? Number(value) : null,
		}));
		setInputFieldIdToErrorMap((prev) => ({
			...prev,
			[fieldId]: '',
		}));
	};

	const handleChangeDateFieldValue = (fieldId: string) => (date: Date | undefined) => {
		setInputFieldIdToValueMap((prev) => ({
			...prev,
			[fieldId]: !!date ? date.toISOString() : null,
		}));
		setInputFieldIdToErrorMap((prev) => ({
			...prev,
			[fieldId]: '',
		}));
	};

	const handleVideoRecorded =
		(fieldId: string) => (videoBlob: Blob, videoPath?: string, duration?: number) => {
			if (videoPath) {
				// videoPath is now just the filename from the video recording component
				let fileName = videoPath;

				// Ensure the filename has the .webm extension
				if (!fileName.endsWith('.webm')) {
					fileName = `${fileName}.webm`;
				}

				setInputFieldIdToValueMap((prev) => ({
					...prev,
					[fieldId]: {
						file_path: `/${fileName}`, // Send just "/" + filename
						file_name: fileName,
						duration: duration || 0, // Duration in seconds
					} as IVideoFileMetadata,
				}));
			} else {
				// Fallback to blob if no S3 path (shouldn't happen in normal flow)

				setInputFieldIdToValueMap((prev) => ({
					...prev,
					[fieldId]: videoBlob,
				}));
			}

			setInputFieldIdToErrorMap((prev) => ({
				...prev,
				[fieldId]: '',
			}));
		};

	const handleVideoRemoved = (fieldId: string) => () => {
		setInputFieldIdToValueMap((prev) => ({
			...prev,
			[fieldId]: null,
		}));
		setInputFieldIdToErrorMap((prev) => ({
			...prev,
			[fieldId]: '',
		}));
	};

	const handleVideoUploadStateChange = (isUploading: boolean) => {
		setIsVideoUploading(isUploading);
	};

	const handleVideoEditModeChange = (fieldId: string) => (isInEditMode: boolean) => {
		setVideoEditModeMap((prev) => ({
			...prev,
			[fieldId]: isInEditMode,
		}));
	};

	const handleVideoUnsavedRecordingChange =
		(fieldId: string) => (hasUnsavedRecording: boolean) => {
			setUnsavedVideoMap((prev) => ({
				...prev,
				[fieldId]: hasUnsavedRecording,
			}));
		};

	const unsavedVideoFieldIds = useMemo(
		() => inputFields.filter((field) => unsavedVideoMap[field.id]).map((field) => field.id),
		[inputFields, unsavedVideoMap]
	);

	const handleGoToUnsavedVideo = () => {
		setShowUnsavedVideoDialog(false);
		emitMixpanelEvent(mixpanelEvents.CLICKED_UNSAVED_VIDEO_GO_TO_VIDEO, {
			feature: MIXPANEL_APP_FEATURES.FORM,
			form_id: formFillDetails?.id,
			form_name: formFillDetails?.name,
		});
		inputFieldIdsToDivMapRef.current[unsavedVideoFieldIds[0]]?.scrollIntoView({
			behavior: 'smooth',
			block: 'center',
		});
	};

	const handleSubmitWithUnsavedVideo = () => {
		setShowUnsavedVideoDialog(false);
		emitMixpanelEvent(mixpanelEvents.CLICKED_UNSAVED_VIDEO_SUBMIT_ANYWAY, {
			feature: MIXPANEL_APP_FEATURES.FORM,
			form_id: formFillDetails?.id,
			form_name: formFillDetails?.name,
			unsaved_video_count: unsavedVideoFieldIds.length,
		});
		handleSubmission();
	};

	const handleRecordingStart = useCallback(
		(fieldId: string) => () => {
			setActiveRecordingFieldId(fieldId);
		},
		[]
	);

	const handleRecordingEnd = useCallback(() => {
		setActiveRecordingFieldId(null);
	}, []);

	const handleChangePhoneFieldValue = (fieldId: string) => (phone: RPNInput.Value | undefined) => {
		setInputFieldIdToValueMap((prev) => ({
			...prev,
			[fieldId]: phone?.toString().trim().length ? phone.toString() : null,
		}));
		setInputFieldIdToErrorMap((prev) => ({
			...prev,
			[fieldId]: '',
		}));
	};

	const handleSubmitForm = () => {
		if (viewOnlyMode || isUploadingFiles) return;

		emitMixpanelEvent(mixpanelEvents.CLICKED_FORM_SUBMIT, {
			feature: MIXPANEL_APP_FEATURES.FORM,
			form_id: formFillDetails?.id,
			form_name: formFillDetails?.name,
		});

		const fieldErrors = validateAndGetErrorsMap();

		const updatedInputFieldIdToErrorMap = Object.keys(fieldErrors).reduce((acc, fieldId) => {
			return {
				...acc,
				[fieldId]: fieldErrors[fieldId]?.[0] ?? '',
			};
		}, inputFieldIdToErrorMap);

		setInputFieldIdToErrorMap(updatedInputFieldIdToErrorMap);

		const fieldIdsWithErrors = inputFields
			.map((field) => {
				if (!!updatedInputFieldIdToErrorMap[field.id]) {
					return field.id;
				}
				return '';
			})
			.filter(Boolean);

		if (fieldIdsWithErrors.length > 0) {
			// Build error details with field names
			const allErrors = fieldIdsWithErrors.map((fieldId) => {
				const fieldName = inputFields.find((f) => f.id === fieldId)?.question || fieldId;
				return `${fieldName}: ${updatedInputFieldIdToErrorMap[fieldId]}`;
			});
			const errorMessages = fieldIdsWithErrors
				.map((fieldId) => updatedInputFieldIdToErrorMap[fieldId])
				.filter(Boolean);

			emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_SUBMIT_ERROR, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formFillDetails?.id,
				form_name: formFillDetails?.name,
				error_count: fieldIdsWithErrors.length,
				all_errors: allErrors.join(', '),
				error_messages: errorMessages.join(', '),
			});

			const elementToScrollTo = inputFieldIdsToDivMapRef.current[fieldIdsWithErrors[0]];

			if (elementToScrollTo) {
				elementToScrollTo.scrollIntoView({
					behavior: 'instant',
					block: 'center',
				});
			}

			return;
		}

		// Required videos are caught above; this only warns about optional ones that were recorded but not saved
		if (unsavedVideoFieldIds.length > 0) {
			emitMixpanelEvent(mixpanelEvents.VIEWED_UNSAVED_VIDEO_WARNING, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formFillDetails?.id,
				form_name: formFillDetails?.name,
				unsaved_video_count: unsavedVideoFieldIds.length,
			});
			setShowUnsavedVideoDialog(true);
			return;
		}

		handleSubmission();
	};

	const validateAndGetErrorsMap = useCallback(
		(fieldValueMapToValidate: IInputFieldIdToValueMap | null = null) => {
			const valuesToValidate = fieldValueMapToValidate ?? inputFieldIdToValueMap;

			// Trim leading/trailing whitespace on string values before validating so a
			// stray space (e.g. an email pasted with a trailing space) doesn't fail an
			// otherwise-valid input. The submit payload already trims, so this keeps
			// validation consistent with what actually gets sent.
			const trimmedValuesToValidate = Object.fromEntries(
				Object.entries(valuesToValidate).map(([fieldId, value]) => [
					fieldId,
					typeof value === 'string' ? value.trim() : value,
				])
			);

			const validationResult = formValidationSchema.safeParse(trimmedValuesToValidate);

			if (validationResult.error) {
				const fieldErrors = validationResult.error.flatten().fieldErrors;
				if (!fieldErrors) return {};
				if (!fieldValueMapToValidate) return fieldErrors;

				const resultedErrorMap: Record<string, string[]> = {};
				for (const key in fieldErrors) {
					if (fieldValueMapToValidate && key in fieldValueMapToValidate)
						resultedErrorMap[key] = fieldErrors[key] ?? [];
				}

				return resultedErrorMap;
			}

			return {};
		},
		[formValidationSchema, inputFieldIdToValueMap]
	);

	const handleInputFieldBlur = useCallback(
		(inputId: string) => {
			const fieldErrors = validateAndGetErrorsMap({
				[inputId]: inputFieldIdToValueMap[inputId],
			});

			const updatedInputFieldIdToErrorMap = Object.keys(fieldErrors).reduce((acc, fieldId) => {
				return {
					...acc,
					[fieldId]: fieldErrors[fieldId]?.[0] ?? '',
				};
			}, inputFieldIdToErrorMap);

			setInputFieldIdToErrorMap(updatedInputFieldIdToErrorMap);
		},
		[validateAndGetErrorsMap, inputFieldIdToValueMap, inputFieldIdToErrorMap]
	);

	useEffect(() => {
		if (!updatedUserDetails) return;

		if (nameInputFieldId) {
			setInputFieldIdToValueMap((prev) => ({
				...prev,
				[nameInputFieldId]: getFullName(updatedUserDetails),
			}));
			setInputFieldIdToErrorMap((prev) => ({
				...prev,
				[nameInputFieldId]: '',
			}));
		}

		if (emailInputFieldId) {
			setInputFieldIdToValueMap((prev) => ({
				...prev,
				[emailInputFieldId]: updatedUserDetails.email,
			}));
			setInputFieldIdToErrorMap((prev) => ({
				...prev,
				[emailInputFieldId]: '',
			}));
		}
	}, [updatedUserDetails, nameInputFieldId, emailInputFieldId]);

	// Track form closed state
	useEffect(() => {
		if (!viewOnlyMode && displayClosedState) {
			emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_CLOSED, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formFillDetails?.id,
				form_name: formFillDetails?.name,
			});
		}
	}, [viewOnlyMode, displayClosedState]);

	// Track form already filled state (only when not just submitted)
	useEffect(() => {
		if (
			!viewOnlyMode &&
			!isFormJustSubmitted &&
			updatedFormFilledByUserDetails?.alreadyFilled &&
			!updatedFormFillDetails?.multipleSubmissions &&
			!displayClosedState
		) {
			emitMixpanelEvent(mixpanelEvents.VIEWED_FORM_ALREADY_FILLED, {
				feature: MIXPANEL_APP_FEATURES.FORM,
				form_id: formFillDetails?.id,
				form_name: formFillDetails?.name,
			});
		}
	}, [
		viewOnlyMode,
		isFormJustSubmitted,
		updatedFormFilledByUserDetails?.alreadyFilled,
		updatedFormFillDetails?.multipleSubmissions,
		displayClosedState,
	]);

	useEffect(() => {
		if (!pendingFormOutdatedDisplayRef.current) return;
		if (isHandlingFormMismatch) return;
		if (inputFields.length === 0) return;

		const fieldErrors = validateAndGetErrorsMap();
		const updatedInputFieldIdToErrorMap = Object.keys(fieldErrors).reduce(
			(acc, fieldId) => ({
				...acc,
				[fieldId]: fieldErrors[fieldId]?.[0] ?? '',
			}),
			inputFieldIdToErrorMap
		);

		const firstFieldIdWithError = inputFields.find(
			(field) => !!updatedInputFieldIdToErrorMap[field.id]
		)?.id;

		if (!firstFieldIdWithError) {
			pendingFormOutdatedDisplayRef.current = false;
			resetFormFillSubmitMutation();
			return;
		}

		const element = inputFieldIdsToDivMapRef.current[firstFieldIdWithError];
		if (!element) return;

		pendingFormOutdatedDisplayRef.current = false;
		setInputFieldIdToErrorMap(updatedInputFieldIdToErrorMap);
		element.scrollIntoView({ behavior: 'instant', block: 'center' });
		resetFormFillSubmitMutation();
	}, [
		inputFields,
		validateAndGetErrorsMap,
		inputFieldIdToErrorMap,
		isHandlingFormMismatch,
		resetFormFillSubmitMutation,
	]);

	return (
		<>
			<div
				className={`word-break min-h-screen w-full overflow-hidden p-8 subpixel-antialiased md:min-h-full md:p-16 ${className}`}
			>
				<FormFillHeader
					logoUrl={formFillDetails?.logo}
					title={formTitle}
				/>
				<FormWidgetsDisplay
					includedWidgets={formFillDetails?.includedWidgets ?? []}
					closingDate={formFillDetails?.closingDate}
					totalCount={formFillDetails?.counts?.totalCount}
					respondedCount={formFillDetails?.counts?.respondedCount}
					formStatus={formFillDetails?.status}
					templateColor={templateColor}
					locations={formFillDetails?.locations}
					variant="minimal"
				/>
				<div className="my-4 md:my-8"></div>
				<FormFillInputsSection
					displaySection={displayFormInputFields}
					key={`displayFormInputFields`}
				>
					{!!formDescription && (
						<div className="w-full px-1">
							<RichTextContent html={formDescription} />
						</div>
					)}

					<div
						className="mb-8 mt-10 flex w-full flex-col gap-6 overflow-hidden px-1"
						key={updatedUserDetails?.email}
					>
						{inputFields.map((inputField) => {
							const error = inputFieldIdToErrorMap[inputField.id];
							// a logged-in user's email is fixed by their account, so it can't be edited
							const disableInputFieldActions =
								disableActions ||
								(!viewOnlyMode && !!updatedUserDetails && inputField.id === emailInputFieldId);

							return (
								<FillingInputField
									key={inputField.id}
									ref={(el) => {
										inputFieldIdsToDivMapRef.current[inputField.id] = el;
									}}
									inputField={{
										...inputField,
										formId: formFillDetails?.id,
										formName: formFillDetails?.name,
										userFirstName: updatedUserDetails?.firstName || 'user',
									}}
									value={inputFieldIdToValueMap[inputField.id]}
									error={error}
									disableActions={disableInputFieldActions}
									onInputFieldChange={handleChangeInputFieldValue(inputField.id)}
									onDateFieldChange={handleChangeDateFieldValue(inputField.id)}
									onNumberFieldChange={handleChangeNumberFieldValue(inputField.id)}
									onPhoneFieldChange={handleChangePhoneFieldValue(inputField.id)}
									onUploadFile={handleUploadFile(inputField.id)}
									onRemoveFile={handleRemoveFile(inputField.id)}
									onFileError={handleFileError(inputField.id)}
									onVideoRecorded={handleVideoRecorded(inputField.id)}
									onVideoRemoved={handleVideoRemoved(inputField.id)}
									onVideoUploadStateChange={handleVideoUploadStateChange}
									onVideoEditModeChange={handleVideoEditModeChange(inputField.id)}
									onVideoUnsavedRecordingChange={handleVideoUnsavedRecordingChange(inputField.id)}
									onRecordingStart={handleRecordingStart(inputField.id)}
									onRecordingEnd={handleRecordingEnd}
									isRecordingBlocked={
										activeRecordingFieldId !== null && activeRecordingFieldId !== inputField.id
									}
									viewOnlyMode={viewOnlyMode}
									isUploading={uploadFileInputFieldIdToStatusMap[inputField.id] === 'pending'}
									autoUpdateValueOnChangeInitialValue={true}
									onBlur={() => handleInputFieldBlur(inputField.id)}
								/>
							);
						})}
					</div>

					{hideSubmitButton ? null : viewOnlyMode ? (
						<p className="ml-1 text-sm text-secondary">{COPY.assignments.previewNotSubmittable}</p>
					) : (
						<div className="z-100 relative ml-1 flex flex-col gap-2">
							<Button
								variant="default"
								className="h-auto w-32 rounded px-0 py-3 text-base"
								onClick={handleSubmitForm}
								disabled={disableActions}
							>
								{isFormFillSubmitLoading ? 'Submitting...' : 'Submit'}
							</Button>
						</div>
					)}
				</FormFillInputsSection>
				<SuccessState
					successMessage={
						isFormJustSubmitted
							? formFillDetails?.formSuccessMessage || null
							: displayMultipleSubmissionsNotAllowedState
								? `Thank you for submitting your response.\n\nThis form is limited to one submission per person. If you need to make changes, please contact the form owner.`
								: null
					}
					displaySuccessState={displaySuccessState}
				/>
				<ClosedState
					closedMessage={formFillDetails?.formCloseMessage}
					displayClosedState={displayClosedState}
				/>
				<InputFieldError errorMessage={formFillSubmitError} />
				<UnsavedVideoDialog
					open={showUnsavedVideoDialog}
					unsavedCount={unsavedVideoFieldIds.length}
					onClose={() => setShowUnsavedVideoDialog(false)}
					onGoToVideo={handleGoToUnsavedVideo}
					onSubmitAnyway={handleSubmitWithUnsavedVideo}
				/>
				{openOtpModal && !!email && !!name && (
					<RegisterModal
						open={openOtpModal}
						onOpenChange={setOpenOtpModal}
						email={email}
						name={name}
						onRegisterSuccess={handleRegisterSuccess}
						formId={formFillDetails?.id}
						formName={formFillDetails?.name}
					/>
				)}
			</div>
		</>
	);
}

export default FillingSection;
